import { useCallback, useState } from 'react'
import { IconCategory, IconType } from '@deepguide-ai/types'
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@deepguide-ai/ui'
import { Input } from '@deepguide-ai/ui/input'
import { Editor } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { NodeViewContent, NodeViewWrapper } from '@tiptap/react'
import { ArrowRight, MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { IconEditor } from '@/components/ui/icon-editor'
import { EmojiPicker } from '@/components/emoji-picker'
import { IconComponent } from '@/components/icon-component'
import {
  SettingsField,
  SettingsPopoverHeader,
} from '@/tiptap/tiptap-ui/settings-popover/settings-popover'
import { CardAttributes } from './card-extension'

interface CardNodeViewProps {
  node: ProseMirrorNode
  editor: Editor
  getPos: () => number | undefined
  updateAttributes: (attributes: Record<string, any>) => void
  deleteNode: () => void
  selected: boolean
}

export function CardNodeView({
  node,
  editor: editor,
  getPos: _getPos,
  updateAttributes,
  deleteNode,
  selected,
}: CardNodeViewProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const attrs = node.attrs as CardAttributes
  const title = attrs.title || 'Card Title'
  const icon = attrs.icon || 'sparkles'
  const iconType = (attrs.iconType || 'REGULAR') as IconType
  const iconCategory = (attrs.iconCategory || 'LUCIDE') as IconCategory
  const iconColor = attrs.iconColor || '#3b82f6'
  const showIcon = attrs.showIcon || false
  const href = attrs.href || ''
  const imagePath = attrs.imagePath || ''
  const callToAction = attrs.callToAction || ''

  // Temporary state for popover editing
  const [tempTitle, setTempTitle] = useState(title)
  const [tempIcon, setTempIcon] = useState(icon)
  const [tempIconType, setTempIconType] = useState(iconType)
  const [tempIconCategory, setTempIconCategory] = useState(iconCategory)
  const [tempIconColor, setTempIconColor] = useState(iconColor)
  const [tempShowIcon, setTempShowIcon] = useState(showIcon)
  const [tempHref, setTempHref] = useState(href)
  const [tempImagePath, setTempImagePath] = useState(imagePath)
  const [tempCallToAction, setTempCallToAction] = useState(callToAction)

  // Function to save settings from popover
  const saveSettings = useCallback(() => {
    const newAttributes: Partial<CardAttributes> = {}

    if (tempTitle.trim() !== title) {
      newAttributes.title = tempTitle.trim() || 'Card Title'
    }

    if (tempIcon !== icon) {
      newAttributes.icon = tempIcon
    }

    if (tempIconType !== iconType) {
      newAttributes.iconType = tempIconType
    }

    if (tempIconCategory !== iconCategory) {
      newAttributes.iconCategory = tempIconCategory
    }

    if (tempIconColor !== iconColor) {
      newAttributes.iconColor = tempIconColor
    }

    if (tempShowIcon !== showIcon) {
      newAttributes.showIcon = tempShowIcon
    }

    if (tempHref !== href) {
      newAttributes.href = tempHref
    }

    if (tempImagePath !== imagePath) {
      newAttributes.imagePath = tempImagePath
    }

    if (tempCallToAction !== callToAction) {
      newAttributes.callToAction = tempCallToAction
    }

    if (Object.keys(newAttributes).length > 0) {
      updateAttributes(newAttributes)
    }

    setIsPopoverOpen(false)
  }, [
    tempTitle,
    title,
    tempIcon,
    icon,
    tempIconType,
    iconType,
    tempIconCategory,
    iconCategory,
    tempIconColor,
    iconColor,
    tempShowIcon,
    showIcon,
    tempHref,
    href,
    tempImagePath,
    imagePath,
    tempCallToAction,
    callToAction,
    updateAttributes,
  ])

  // Function to cancel settings changes
  const cancelSettings = useCallback(() => {
    setTempTitle(title)
    setTempIcon(icon)
    setTempIconType(iconType)
    setTempIconCategory(iconCategory)
    setTempIconColor(iconColor)
    setTempShowIcon(showIcon)
    setTempHref(href)
    setTempImagePath(imagePath)
    setTempCallToAction(callToAction)
    setIsPopoverOpen(false)
  }, [
    title,
    icon,
    iconType,
    iconCategory,
    iconColor,
    showIcon,
    href,
    imagePath,
    callToAction,
  ])

  // Function to open settings popover
  const openSettings = useCallback(() => {
    setTempTitle(title)
    setTempIcon(icon)
    setTempIconType(iconType)
    setTempIconCategory(iconCategory)
    setTempIconColor(iconColor)
    setTempShowIcon(showIcon)
    setTempHref(href)
    setTempImagePath(imagePath)
    setTempCallToAction(callToAction)
    setIsPopoverOpen(true)
  }, [
    title,
    icon,
    iconType,
    iconCategory,
    iconColor,
    showIcon,
    href,
    imagePath,
    callToAction,
  ])

  // Handle delete card
  const handleDeleteCard = useCallback(() => {
    deleteNode()
  }, [deleteNode])

  return (
    <NodeViewWrapper
      className={cn(
        'group/card relative p-0',
        selected && 'ProseMirror-selectednode'
      )}
      data-type='Card'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className='card relative my-5 h-full overflow-hidden rounded-md border border-[var(--border)] transition-shadow hover:shadow-md'>
        {/* Card header with image - only show if imagePath is provided */}
        {imagePath && (
          <div className='card-header bg-border border-b'>
            <img src={imagePath} className='w-full' alt={title} />
          </div>
        )}

        {/* Settings button for cards without images - appears on hover */}
        {editor.isEditable && (isHovered || isPopoverOpen) && (
          <div className='absolute top-2 right-2 z-10'>
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='ghost'
                  size='menuAction'
                  onClick={(e) => {
                    e.stopPropagation()
                    openSettings()
                  }}
                  className='text-muted-foreground hover:bg-accent p-1'
                >
                  <MoreHorizontal className='h-4 w-4' />
                </Button>
              </PopoverTrigger>

              <PopoverContent
                className='w-80 p-4 pb-3'
                align='end'
                side='bottom'
                sideOffset={8}
              >
                <div className='space-y-4'>
                  <SettingsPopoverHeader
                    title='Edit Card Attributes'
                    onClose={cancelSettings}
                  />

                  <div className='space-y-4'>
                    <SettingsField
                      label={
                        <>
                          Title <span className='text-destructive'>*</span>
                        </>
                      }
                    >
                      <div className='flex items-center gap-2'>
                        <EmojiPicker
                          selectedIcon={tempShowIcon ? tempIcon : undefined}
                          iconColor={tempIconColor}
                          iconCategory={tempIconCategory}
                          onEmojiSelect={(emoji) => {
                            setTempShowIcon(true)
                            setTempIcon(emoji)
                            setTempIconCategory('EMOJI')
                          }}
                          onIconChange={(
                            iconName: string,
                            iconCategory: IconCategory
                          ) => {
                            if (iconName === '') {
                              setTempIconCategory('LUCIDE')
                              setTempShowIcon(false)
                              return
                            }
                            setTempShowIcon(true)
                            setTempIcon(iconName)
                            setTempIconCategory(iconCategory)
                          }}
                          onColorChange={setTempIconColor}
                          isPopoverMode={true}
                        />
                        <Input
                          type='text'
                          value={tempTitle}
                          onChange={(e) => setTempTitle(e.target.value)}
                          placeholder='Enter title...'
                          className='flex-1'
                        />
                      </div>
                    </SettingsField>

                    {/* Icon Settings */}
                    <div>
                      {/* <div className='flex justify-between items-center mb-5'>
                        <label className='text-xs leading-4 text-muted-foreground'>
                          Show Icon
                        </label>
                        <Switch
                          checked={tempShowIcon}
                          onCheckedChange={() => setTempShowIcon(!tempShowIcon)}
                        />
                      </div> */}

                      {/* Icon Editor - only show if showIcon is true */}
                      {/* {tempShowIcon && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <IconEditor
                            selectedIcon={tempIcon}
                            iconType={tempIconType}
                            iconCategory={tempIconCategory}
                            iconColor={tempIconColor}
                            onIconChange={setTempIcon}
                            onTypeChange={setTempIconType}
                            onCategoryChange={setTempIconCategory}
                            onColorChange={setTempIconColor}
                          />
                        </div>
                      )} */}
                      {/* <div className='flex flex-col gap-2'>
                        <label className='text-muted-foreground text-xs leading-4'>
                          Icon
                        </label>
                        <EmojiPicker
                          selectedIcon={tempShowIcon ? tempIcon : undefined}
                          iconColor={tempIconColor}
                          iconCategory={tempIconCategory}
                          onEmojiSelect={(emoji) => {
                            setTempShowIcon(true)
                            setTempIcon(emoji)
                            setTempIconCategory('EMOJI')
                          }}
                          onIconChange={(
                            iconName: string,
                            iconCategory: IconCategory
                          ) => {
                            if (iconName === '') {
                              setTempIconCategory('LUCIDE')
                              setTempShowIcon(false)
                              return
                            }
                            setTempShowIcon(true)
                            setTempIcon(iconName)
                            setTempIconCategory(iconCategory)
                          }}
                          onColorChange={setTempIconColor}
                          isPopoverMode={true}
                        />
                      </div> */}
                    </div>

                    <SettingsField label='URL'>
                      <Input
                        type='url'
                        value={tempHref}
                        onChange={(e) => setTempHref(e.target.value)}
                        placeholder='Enter URL...'
                      />
                    </SettingsField>

                    <SettingsField label='Image Path'>
                      <Input
                        type='text'
                        value={tempImagePath}
                        onChange={(e) => setTempImagePath(e.target.value)}
                        placeholder='Enter image URL or path...'
                      />
                    </SettingsField>

                    <SettingsField label='Call To Action'>
                      <Input
                        type='text'
                        value={tempCallToAction}
                        onChange={(e) => setTempCallToAction(e.target.value)}
                        placeholder='Enter Call To Action...'
                      />
                    </SettingsField>
                  </div>
                  <Separator className='!mt-5 !mb-3 -ml-4 !w-[calc(100%+32px)]' />

                  <div className='flex items-center justify-between'>
                    <Button
                      variant='destructive'
                      className='h-8 !bg-transparent px-2 py-1.5 text-[var(--destructive)] shadow-none'
                      onClick={handleDeleteCard}
                    >
                      Delete
                    </Button>
                    <div className='flex items-center space-x-2'>
                      <Button
                        variant='outline'
                        className='h-8 px-2 py-1.5'
                        onClick={cancelSettings}
                      >
                        Cancel
                      </Button>
                      <Button
                        className='h-8 px-2 py-1.5'
                        onClick={saveSettings}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}

        <div className='card-content px-6 py-5'>
          <h4
            className='mb-2 flex flex-col items-start gap-3 text-lg font-medium'
            contentEditable={false}
          >
            {/* Show icon if enabled */}
            {showIcon && (
              <IconComponent
                iconName={icon}
                iconType={iconType}
                iconCategory={iconCategory}
                iconColor={iconColor}
                size={24}
                className={`transition-colors ${
                  iconCategory === 'EMOJI' ? 'h-6 w-6' : ''
                }`}
              />
            )}
            {title}
          </h4>

          <NodeViewContent />
        </div>

        {(href || callToAction) && (
          <a
            href={href || '#'}
            className='flex items-center truncate border-t p-3 text-sm text-blue-500 !no-underline transition-colors hover:bg-blue-50'
            contentEditable={false}
          >
            {callToAction || 'Learn more'}{' '}
            <ArrowRight className='ml-1 h-4 w-4' />
          </a>
        )}
      </div>
    </NodeViewWrapper>
  )
}
